import { useEffect } from 'react';
import {
  getStartEndDateForContent,
  validateLiveContent,
} from '@tbx/experience-widgets-lib';

function pad2(n) {
  return String(n).padStart(2, '0');
}
function pad3(n) {
  return String(n).padStart(3, '0');
}
function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

function fallbackString(v) {
  return isNonEmptyString(v) ? v : '*null';
}

const formatDate = (date) => {
  try {
    if (!date) return '*null';
    return new Date(date).toISOString().slice(0, 10);
  } catch (e) {
    return '*null';
  }
};

function buildComscoreContentMetadata(analytics, data, genderConfig) {
  const cm = new analytics.StreamingAnalytics.ContentMetadata();
  const isLive = validateLiveContent(data);
  const durationSeconds = Number(data?.duration);

  cm.setDictionaryClassificationC3('CANALRCN.COM');
  cm.setDictionaryClassificationC4('*null');
  cm.setDictionaryClassificationC6('*null');
  const mediaType = isLive
    ? analytics?.StreamingAnalytics?.ContentMetadata?.ContentType?.LIVE
    : durationSeconds < 600
      ? analytics?.StreamingAnalytics?.ContentMetadata?.ContentType
          ?.SHORT_FORM_ON_DEMAND
      : analytics?.StreamingAnalytics?.ContentMetadata?.ContentType
          ?.LONG_FORM_ON_DEMAND;

  cm.setMediaType(mediaType);
  const durationMs =
    Number.isFinite(durationSeconds) && durationSeconds >= 0
      ? Math.floor(durationSeconds * 1000)
      : 0;
  cm.setLength(durationMs);

  const uniqueId = isNonEmptyString(data?.externalId)
    ? data.externalId
    : isNonEmptyString(data?.id)
      ? data.id
      : '*null';
  cm.setUniqueId(uniqueId);

  const programTitle = data?.serie?.title;
  cm.setProgramTitle(isLive ? 'live' : fallbackString(programTitle));

  const episodeTitle = data?.title;
  cm.setEpisodeTitle(fallbackString(episodeTitle));

  const isCompleteEpisode =
    data?.contentType === 'EPISODE' &&
    data?.clip === false &&
    data?.trailerConfig?.isTrailer === false;
  cm.classifyAsCompleteEpisode(!!isCompleteEpisode);

  cm.setGenreName(fallbackString(genderConfig)); // ns_st_ge
  cm.setStationTitle('CanalRCN'); // ns_st_st
  cm.setPublisherName('RCN'); // ns_st_pu

  const seasonNum = Number(data?.season);
  const seasonStr =
    Number.isFinite(seasonNum) && seasonNum > 0 ? pad2(seasonNum) : '*null';
  cm.setEpisodeSeasonNumber(seasonStr);

  const epNum = Number(data?.episode);
  const epStr = Number.isFinite(epNum) && epNum > 0 ? pad3(epNum) : '*null';
  cm.setEpisodeNumber(epStr);

  cm.carryTvAdvertisementLoad(false);

  const flightStart = getStartEndDateForContent(data);
  if (isNonEmptyString(flightStart)) {
    const d = new Date(flightStart);
    if (!Number.isNaN(d.getTime())) {
      cm.setDateOfTvAiring(
        d.getUTCFullYear(),
        d.getUTCMonth() + 1,
        d.getUTCDate(),
      );
    }
  }
  cm.addCustomLabels({
    ns_st_ddt: formatDate(getStartEndDateForContent(data).start),
    ns_st_tdt: formatDate(getStartEndDateForContent(data)?.start),
  });

  return cm;
}

export const useComscoreStreaming = (
  player,
  publisherId,
  metadata,
  genderConfig,
) => {
  useEffect(() => {
    if (!player || !window?.ns_?.analytics?.StreamingAnalytics) return;
    const StreamingAnalytics =
      window.ns_.analytics.StreamingAnalytics || window?.analytics;

    const sa = new StreamingAnalytics();
    const analytics = window?.ns_?.analytics;

    // Initialize Comscore Analytics
    analytics.PlatformApi.setPlatformAPI(
      analytics.PlatformApi.PlatformApis.WebBrowser,
    );
    const myPublisherConfig =
      new analytics.configuration.PublisherConfiguration({
        publisherId,
        persistentLabels: {
          ns_st_st: 'CanalRCN',
          ns_st_at: 'es_ES',
          ns_st_vt: 'en_EN',
        },
      });
    analytics.configuration.addClient(myPublisherConfig);
    analytics.start();

    // Set player information
    sa.setMediaPlayerName('videojs');
    sa.setMediaPlayerVersion('1.0.0');
    sa.createPlaybackSession();

    // Set content metadata
    const cm = buildComscoreContentMetadata(analytics, metadata, genderConfig);

    // Set the content metadata for the session
    sa.setMetadata(cm);

    // playback events
    const onPlay = () => {
      sa.notifyPlay();
    };
    const onPause = () => {
      sa.notifyPause();
    };
    const onBufferStart = () => {
      sa.notifyBufferStart();
    };
    const onBufferStop = () => {
      sa.notifyBufferStop();
    };
    const onSeekStart = () => {
      sa.notifySeekStart();
    };
    const onEnded = () => {
      sa.notifyEnd();
    };
    const onTimeUpdate = () => {
      const position = Math.floor(player.currentTime() * 1000);
      sa.startFromPosition(position);
    };

    const onPageHideOrUnload = (event) => {
      // localStorage.setItem(
      //   'comscore_onPageHideOrUnload',
      //   Date.now().toString(),
      // );
      // event.preventDefault();
      // event.returnValue = '';
      sa.notifyEnd();
    };

    const onBeforeUnload = (event) => {
      // localStorage.setItem('comscore_onBeforeUnload', Date.now().toString());
      // event.preventDefault();
      // event.returnValue = '';
      sa.notifyEnd();
    };

    const onDispose = () => {
      sa.notifyEnd();
    };

    // Ad events - This is a simplified example. You may need to adjust it based on how your ad integration works.
    // const onAdsAdStarted = () => {
    //   if (!analytics || !player) return;

    //   const adsManager = player?.ima?.getAdsManager();
    //   const ad = adsManager?.getCurrentAd();
    //   const pod = ad?.getAdPodInfo();
    //   const timeOffset = pod?.getTimeOffset();

    //   const durationMs = ad?.getDuration() * 1000 || 0;
    //   let adType = 'midroll';
    //   if (timeOffset === 0) {
    //     adType = 'preroll';
    //   } else if (timeOffset === -1) {
    //     adType = 'postroll';
    //   }

    //   const adTypeMap = {
    //     preroll:
    //       analytics?.StreamingAnalytics.AdvertisementMetadata.AdvertisementType
    //         .ON_DEMAND_PRE_ROLL,
    //     midroll:
    //       analytics?.StreamingAnalytics.AdvertisementMetadata.AdvertisementType
    //         .ON_DEMAND_MID_ROLL,
    //     postroll:
    //       analytics?.StreamingAnalytics.AdvertisementMetadata.AdvertisementType
    //         .ON_DEMAND_POST_ROLL,
    //   };

    //   const am = new analytics.StreamingAnalytics.AdvertisementMetadata();
    //   am.setMediaType(adTypeMap[adType]);
    //   am.setRelatedContentMetadata(cm);
    //   am.setLength(durationMs);
    //   sa.setMetadata(am);
    //   sa.notifyPlay();
    // };

    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('waiting', onBufferStart);
    player.on('playing', onBufferStop);
    player.on('seeking', onSeekStart);
    player.on('ended', onEnded);
    player.on('timeupdate', onTimeUpdate);
    window.addEventListener('pagehide', onPageHideOrUnload);
    window.addEventListener('beforeunload', onBeforeUnload);
    player.on('dispose', onDispose);

    // Ad events
    // player.on('ads-ad-started', onAdsAdStarted);
    // player.on('adstart', onAdsAdStarted);

    // player.on('adstart', () => {
    //   console.log('Ad started', player);
    // });

    // player.on('adend', () => {
    //   console.log('Ad finished', player);
    // });

    return () => {
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('waiting', onBufferStart);
      player.off('playing', onBufferStop);
      player.off('seeking', onSeekStart);
      player.off('ended', onEnded);
      player.off('timeupdate', onTimeUpdate);
      window.removeEventListener('pagehide', onPageHideOrUnload);
      window.removeEventListener('beforeunload', onBeforeUnload);
      player.off('dispose', onDispose);
      // player.off('adstart', onAdsAdStarted);
    };
  }, [player, metadata]);
};
